import { adminService } from "@/api/admin/admin.service";
import type { TAdminFile, TFileStatsOverview } from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { Loader2, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const PAGE_SIZE = 20;

function fmtBytes(bytes?: number | null): string {
  if (bytes == null) return "-";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function StatCard({ title, value }: { title: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      </CardContent>
    </Card>
  );
}

export function FilesPage() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);
  const [overview, setOverview] = useState<TFileStatsOverview | null>(null);
  const [byType, setByType] = useState<Record<string, number> | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<TAdminFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadStats = useCallback(async (signal: AbortSignal) => {
    const [overviewRes, byTypeRes] = await Promise.all([
      adminService.getFileStatsOverview({ signal }),
      adminService.getFileStatsByType({ signal }),
    ]);
    if (signal.aborted) return;
    if (!overviewRes.error && overviewRes.data) setOverview(overviewRes.data.data ?? null);
    if (!byTypeRes.error && byTypeRes.data) setByType(byTypeRes.data.data ?? null);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadStats(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats, reloadTick]);

  const fetcher = async (query: TDataGridQuery) => {
    const response = await adminService.getFiles(
      {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const columns: TColumnDef<TAdminFile>[] = [
    {
      id: "original_filename",
      header: "Filename",
      cell: (f) => (
        <div>
          <div className="text-sm font-medium text-foreground">{f.original_filename}</div>
          <div className="font-mono text-xs text-muted-foreground">{f.id}</div>
        </div>
      ),
    },
    { id: "file_type", header: "Type", type: "badge", sortable: true, sortKey: "file_type" },
    {
      id: "file_size",
      header: "Size",
      align: "right",
      sortable: true,
      sortKey: "file_size",
      cell: (f) => <span className="tabular-nums text-foreground">{fmtBytes(f.file_size)}</span>,
    },
    { id: "content_type", header: "Content type", type: "text", hideable: true, defaultHidden: true },
    {
      id: "uploaded_by",
      header: "Uploaded by",
      type: "uuid",
      hideable: true,
      accessor: (f) => f.uploaded_by ?? "",
    },
    { id: "created_at", header: "Uploaded", type: "date", sortable: true, sortKey: "created_at" },
  ];

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const { error: err } = await adminService.deleteFile(deleteTarget.id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to delete file",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "File deleted" });
      setDeleteTarget(null);
      setReloadTick((t) => t + 1);
    }
    setIsDeleting(false);
  };

  const typeSummary = byType
    ? Object.entries(byType)
        .map(([k, v]) => `${k}: ${v}`)
        .join(" • ")
    : "";

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Files</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Browse and manage uploaded files across the platform
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setReloadTick((t) => t + 1)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard title="Total Files" value={overview?.total_files ?? 0} />
          <StatCard title="Total Size" value={fmtBytes(overview?.total_size_bytes)} />
          <StatCard title="Types Tracked" value={byType ? Object.keys(byType).length : 0} />
        </div>
        <p className="text-xs text-muted-foreground">
          {typeSummary || "By-type breakdown"}
          <span className="ml-2 italic">
            (file stats endpoints are backend stubs and currently report zeros)
          </span>
        </p>

        <DataGrid<TAdminFile>
          getRowId={(row) => row.id}
          persistKey="admin.files"
          ariaLabel="Files"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search filename or content type",
          }}
          rowActions={(file) => [
            {
              label: "Delete",
              icon: <Trash2 className="h-4 w-4" />,
              variant: "destructive",
              onClick: () => setDeleteTarget(file),
            },
          ]}
          emptyState={{ title: "No files found." }}
        />
      </div>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete “{deleteTarget?.original_filename}”? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={isDeleting}>
              {isDeleting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
